import { useAuth } from "../../../hooks/useAuth";
import { MESSAGE_CHANNELS } from "../../../config/messageChannels";
import { WORKFLOWS } from "../../../config/workflows";
import { RequestedAppsSummary, useServableApps } from "../RequestedApps";
import { useOnboardingDraft } from "../OnboardingContext";
import styles from "../OnboardingWizard.module.css";
import type { StepProps } from "../OnboardingWizard";

const orDash = (value: string | null | undefined): string =>
  value && value.length > 0 ? value : "-";

const titleCase = (value: string): string =>
  value.length === 0 ? value : value[0]!.toUpperCase() + value.slice(1);

export const ReviewStep = ({
  onBack,
  canBack,
  onJumpTo,
  onSubmit,
  submitting,
  submitError,
}: StepProps): JSX.Element => {
  const { draft } = useOnboardingDraft();
  const { user } = useAuth();
  // Gate on what the catalog can actually serve, so the section header never
  // survives an empty list.
  const apps = useServableApps(user?.requestedIntegrations ?? []);
  const personalities = draft.personalities ?? [];
  const channel = MESSAGE_CHANNELS.find((c) => c.slug === draft.preferredChannel);
  const workflow = WORKFLOWS.find((w) => w.slug === draft.selectedWorkflow);

  return (
    <section className={styles.card}>
      <div className={styles.stepHeader}>
        <h2 className={styles.stepTitle}>One last look</h2>
        <p className={styles.stepHint}>
          Edit anything that's off, then submit. We'll get to work.
        </p>
      </div>

      <div className={styles.reviewSection}>
        <div className={styles.reviewHeader}>
          <span className={styles.reviewTitle}>Agent</span>
          <button type="button" className={styles.reviewEdit} onClick={() => onJumpTo("agent")}>Edit</button>
        </div>
        <div className={styles.reviewBody}>
          <span>
            <strong>{orDash(draft.agentName)}</strong>
            {personalities.length > 0
              ? ` · ${personalities.map(titleCase).join(", ")}`
              : ""}
          </span>
        </div>
      </div>

      <div className={styles.reviewSection}>
        <div className={styles.reviewHeader}>
          <span className={styles.reviewTitle}>About you</span>
          <button type="button" className={styles.reviewEdit} onClick={() => onJumpTo("about")}>Edit</button>
        </div>
        <div className={styles.reviewBody}>
          {draft.preferredName ? <span>Calls you <strong>{draft.preferredName}</strong></span> : null}
          {draft.businessDescription ? <span className={styles.reviewMeta}>{draft.businessDescription}</span> : null}
          {draft.agentBoundaries ? <span className={styles.reviewMeta}>Never: {draft.agentBoundaries}</span> : null}
          {!draft.preferredName && !draft.businessDescription && !draft.agentBoundaries ? (
            <span className={styles.reviewMeta}>Skipped - your agent will ask when you first chat.</span>
          ) : null}
        </div>
      </div>

      <div className={styles.reviewSection}>
        <div className={styles.reviewHeader}>
          <span className={styles.reviewTitle}>Timezone</span>
          <button type="button" className={styles.reviewEdit} onClick={() => onJumpTo("agent")}>Edit</button>
        </div>
        <div className={styles.reviewBody}>
          <span>{orDash(draft.timezone).replace(/_/g, " ")}</span>
        </div>
      </div>

      <div className={styles.reviewSection}>
        <div className={styles.reviewHeader}>
          <span className={styles.reviewTitle}>Channel</span>
          <button type="button" className={styles.reviewEdit} onClick={() => onJumpTo("channel")}>Edit</button>
        </div>
        <div className={styles.reviewBody}>
          <span>{channel ? channel.name : "-"}</span>
          {channel?.slug === "whatsapp" ? (
            <span className={styles.reviewMeta}>Needs its own phone number - have a spare SIM or eSIM ready.</span>
          ) : null}
        </div>
      </div>

      {apps.visible.length > 0 ? (
        <div className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <span className={styles.reviewTitle}>Apps</span>
          </div>
          <div className={styles.reviewBody}>
            <RequestedAppsSummary {...apps} />
            <span className={styles.reviewMeta}>
              We&apos;ve set these up for you to connect - you&apos;ll get links the moment your
              instance is on its way.
            </span>
          </div>
        </div>
      ) : null}

      {workflow ? (
        <div className={styles.reviewSection}>
          <div className={styles.reviewHeader}>
            <span className={styles.reviewTitle}>Workflow</span>
            <button type="button" className={styles.reviewEdit} onClick={() => onJumpTo("workflow")}>Edit</button>
          </div>
          <div className={styles.reviewBody}>
            <span>{workflow.name}</span>
            <span className={styles.reviewMeta}>{workflow.blurb}</span>
          </div>
        </div>
      ) : null}

      <div className={styles.reviewSection}>
        <div className={styles.reviewHeader}>
          <span className={styles.reviewTitle}>AI</span>
          <button type="button" className={styles.reviewEdit} onClick={() => onJumpTo("ai")}>Edit</button>
        </div>
        <div className={styles.reviewBody}>
          {draft.aiSetupMethod === "openai-subscription" ? (
            <>
              <span>OpenAI subscription</span>
              <span className={styles.reviewMeta}>
                We'll connect it with you on your onboarding call.
              </span>
            </>
          ) : (
            <>
              <span>OpenRouter</span>
              {draft.openrouterApiKey ? (
                <span className={styles.reviewMeta}>API key set</span>
              ) : null}
            </>
          )}
        </div>
      </div>

      {submitError && <div className={styles.errorBanner}>{submitError}</div>}

      <div className={styles.footer}>
        <button type="button" className={styles.secondaryBtn} onClick={onBack} disabled={!canBack || submitting}>Back</button>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => { void onSubmit(); }}
          disabled={submitting}
        >
          {submitting ? "Submitting…" : "Submit"}
        </button>
      </div>
    </section>
  );
};
